'use client';

import { useMemo, useState } from 'react';
import type { FilterCategory, Project } from '@/lib/projects';
import { FILTER_TABS, filterProjects } from '@/lib/projects';
import { ProjectFilters } from './ProjectFilters';
import { FeaturedProjectCard } from './FeaturedProjectCard';
import { SmallProjectCard } from './SmallProjectCard';
import { ScrollReveal } from '@/components/ui/ScrollReveal';

type Variant = 'featured' | 'all';

type Props = {
  projects: Project[];
  variant: Variant;
};

function buildCounts(projects: Project[]): Record<FilterCategory, number> {
  const counts: Record<FilterCategory, number> = {
    all: projects.length,
    saas: 0,
    fintech: 0,
    gaming: 0,
    compliance: 0,
    gamedev: 0,
    systems: 0,
    freelance: 0,
  };
  for (const p of projects) counts[p.category] += 1;
  return counts;
}

export function ProjectsExplorer({ projects, variant }: Props) {
  const [filter, setFilter] = useState<FilterCategory>('all');
  const counts = useMemo(() => buildCounts(projects), [projects]);
  const filtered = useMemo(() => filterProjects(projects, filter), [projects, filter]);

  void FILTER_TABS;

  const featured = filtered.filter((p) => p.featured);
  const regular = filtered.filter((p) => !p.featured);

  return (
    <div className="flex flex-col gap-10 md:gap-14">
      <ProjectFilters value={filter} onChange={setFilter} counts={counts} />

      {filtered.length === 0 && (
        <p className="text-center text-text-muted font-mono text-sm">
          No projects in this category yet.
        </p>
      )}

      {variant === 'featured' ? (
        <>
          {featured.length > 0 && (
            <div className="flex flex-col gap-8 md:gap-10">
              {featured.map((project, i) => (
                <ScrollReveal key={project.slug} delay={i * 80} direction="up">
                  <FeaturedProjectCard project={project} index={i} />
                </ScrollReveal>
              ))}
            </div>
          )}

          {regular.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regular.map((project, i) => (
                <ScrollReveal
                  key={project.slug}
                  delay={(i % 3) * 80}
                  direction="up"
                  className="h-full"
                >
                  <SmallProjectCard project={project} />
                </ScrollReveal>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((project, i) => (
            <ScrollReveal
              key={project.slug}
              delay={(i % 2) * 80}
              direction="up"
              className="h-full"
            >
              <SmallProjectCard project={project} />
            </ScrollReveal>
          ))}
        </div>
      )}
    </div>
  );
}
